/**
 * ☰ Admin › Ledger — what each user's Summaries cost.
 *
 * `docs/features/summary.md` § "Every charge is a ledger row, per user". One row
 * per user charged in the period, highest billed first; a click opens that
 * user's charges, newest first. The period defaults to 7 days (owner,
 * 2026-10-17). The model is shown here because this is admins only — a reader
 * never sees it.
 *
 * Usage's shape on purpose (`AdminUsage.tsx`): the table scrolls sideways inside
 * itself, the panel around it never does.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { plainText } from "@qufolio/screener-grid";

import { fetchLedgerCharges, fetchLedgerUsers, type LedgerCharge, type LedgerUser } from "../api/ledger";
import { formatWhen } from "../lib/activity";

/** Days; `null` is every row. */
const PERIODS: { days: number | null; label: string }[] = [
  { days: 7, label: "Last 7 days" },
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" },
  { days: 365, label: "Last 365 days" },
  { days: null, label: "All" },
];

const BAR: React.CSSProperties = { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" };
const NOTE: React.CSSProperties = { color: "var(--qf-text-secondary)", fontSize: 12 };
const MUTED: React.CSSProperties = { color: "var(--qf-text-muted)", fontSize: 12 };
const ERROR: React.CSSProperties = { ...NOTE, color: "var(--qf-negative, #d66)" };
const SCROLL: React.CSSProperties = { overflowX: "auto", maxWidth: "100%" };
const TABLE: React.CSSProperties = { borderCollapse: "collapse", width: "100%", fontSize: 12 };
const TH: React.CSSProperties = {
  textAlign: "left",
  fontWeight: 500,
  color: "var(--qf-text-muted)",
  padding: "4px 8px",
  borderBottom: "1px solid var(--qf-border)",
  whiteSpace: "nowrap",
  cursor: "pointer",
  userSelect: "none",
};
const TD: React.CSSProperties = { padding: "4px 8px", whiteSpace: "nowrap", verticalAlign: "top" };
const NUM: React.CSSProperties = { ...TD, textAlign: "right", fontVariantNumeric: "tabular-nums" };
const TOTAL: React.CSSProperties = { borderTop: "1px solid var(--qf-border)", fontWeight: 500 };
const INPUT: React.CSSProperties = {
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  padding: "3px 8px",
  fontSize: 12,
};

type SortKey = "billedUsd" | "charges" | "listCostUsd" | "inputTokens" | "lastAt";

const COLUMNS: { key: SortKey; label: string; title?: string }[] = [
  { key: "charges", label: "Charges", title: "Model calls that succeeded — a stored note is free" },
  { key: "billedUsd", label: "Billed", title: "What the user was charged" },
  { key: "listCostUsd", label: "List", title: "What the vendor charged us" },
  { key: "inputTokens", label: "Tokens in / out" },
  { key: "lastAt", label: "Last charge" },
];

export function usd(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : `$${value.toFixed(4)}`;
}

function tokens(input: number, output: number): string {
  return `${input.toLocaleString("en-US")} / ${output.toLocaleString("en-US")}`;
}

export function AdminLedger() {
  const [days, setDays] = useState<number | null>(7);
  const [users, setUsers] = useState<LedgerUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("billedUsd");
  const [open, setOpen] = useState<string | null>(null);

  const reload = useCallback(() => {
    setError(null);
    fetchLedgerUsers(days)
      .then(setUsers)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [days]);
  useEffect(reload, [reload]);

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const rows = (users ?? []).filter(
      (u) => !needle || [u.email, u.userKey].some((v) => v?.toLowerCase().includes(needle)),
    );
    return [...rows].sort((a, b) =>
      sort === "lastAt" ? (b.lastAt ?? "").localeCompare(a.lastAt ?? "") : b[sort] - a[sort],
    );
  }, [users, search, sort]);

  const total = useMemo(
    () =>
      shown.reduce(
        (sum, u) => ({
          charges: sum.charges + u.charges,
          billedUsd: sum.billedUsd + u.billedUsd,
          listCostUsd: sum.listCostUsd + u.listCostUsd,
          inputTokens: sum.inputTokens + u.inputTokens,
          outputTokens: sum.outputTokens + u.outputTokens,
        }),
        { charges: 0, billedUsd: 0, listCostUsd: 0, inputTokens: 0, outputTokens: 0 },
      ),
    [shown],
  );

  return (
    <>
      <div style={BAR}>
        <input
          style={{ ...INPUT, flex: "1 1 160px", minWidth: 0 }}
          placeholder="Search email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search users"
        />
        <select
          style={INPUT}
          value={days ?? "all"}
          onChange={(e) => setDays(e.target.value === "all" ? null : Number(e.target.value))}
          aria-label="Period"
        >
          {PERIODS.map((p) => (
            <option key={p.label} value={p.days ?? "all"}>
              {p.label}
            </option>
          ))}
        </select>
        <button type="button" style={INPUT} onClick={reload}>
          Refresh
        </button>
      </div>
      <div style={MUTED}>
        Opening a stored note is free and is not here.
      </div>

      {error && <div style={ERROR}>{plainText(error)}</div>}
      {users === null && !error && <div style={MUTED}>Loading…</div>}
      {users !== null && shown.length === 0 && (
        <div style={NOTE}>{users.length === 0 ? "Nobody was charged in this period." : "No match."}</div>
      )}

      {shown.length > 0 && (
        <div style={SCROLL}>
          <table style={TABLE}>
            <thead>
              <tr>
                <th style={{ ...TH, cursor: "default" }}>User</th>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    style={{
                      ...TH,
                      textAlign: c.key === "lastAt" ? "left" : "right",
                      color: sort === c.key ? "var(--qf-text)" : TH.color,
                    }}
                    title={c.title ? `${c.title} — click to sort` : "Click to sort"}
                    onClick={() => setSort(c.key)}
                  >
                    {c.label}
                    {sort === c.key ? " ▾" : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((u) => (
                <UserRows
                  key={u.userKey}
                  user={u}
                  days={days}
                  open={open === u.userKey}
                  onToggle={() => setOpen(open === u.userKey ? null : u.userKey)}
                />
              ))}
              <tr>
                <td style={{ ...TD, ...TOTAL }}>Total</td>
                <td style={{ ...NUM, ...TOTAL }}>{total.charges}</td>
                <td style={{ ...NUM, ...TOTAL }}>{usd(total.billedUsd)}</td>
                <td style={{ ...NUM, ...TOTAL }}>{usd(total.listCostUsd)}</td>
                <td style={{ ...NUM, ...TOTAL }}>{tokens(total.inputTokens, total.outputTokens)}</td>
                <td style={{ ...TD, ...TOTAL }} />
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function UserRows({
  user,
  days,
  open,
  onToggle,
}: {
  user: LedgerUser;
  days: number | null;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        style={{ cursor: "pointer", background: open ? "var(--qf-selection)" : undefined }}
        aria-expanded={open}
      >
        <td style={TD} title={user.userKey}>
          {open ? "▾ " : "▸ "}
          {user.email ?? user.userKey}
        </td>
        <td style={NUM}>{user.charges}</td>
        <td style={NUM}>{usd(user.billedUsd)}</td>
        <td style={NUM}>{usd(user.listCostUsd)}</td>
        <td style={NUM}>{tokens(user.inputTokens, user.outputTokens)}</td>
        <td style={TD}>{formatWhen(user.lastAt)}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6} style={{ ...TD, whiteSpace: "normal", paddingLeft: 20 }}>
            <Charges userKey={user.userKey} days={days} />
          </td>
        </tr>
      )}
    </>
  );
}

const PART: Record<string, string> = { summary: "note", "summary-personal": "personal", "macro-note": "macro note" };

function Charges({ userKey, days }: { userKey: string; days: number | null }) {
  const [rows, setRows] = useState<LedgerCharge[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetchLedgerCharges(userKey, days)
      .then((r) => alive && setRows(r))
      .catch((e: unknown) => alive && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      alive = false;
    };
  }, [userKey, days]);

  if (error) return <div style={ERROR}>{plainText(error)}</div>;
  if (!rows) return <div style={MUTED}>Loading…</div>;
  return (
    <table style={TABLE}>
      <thead>
        <tr>
          {["Time", "Part", "Symbol", "Model"].map((h) => (
            <th key={h} style={{ ...TH, cursor: "default" }}>
              {h}
            </th>
          ))}
          {["Billed", "List", "In / out"].map((h) => (
            <th key={h} style={{ ...TH, cursor: "default", textAlign: "right" }}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={`${r.at}-${i}`}>
            <td style={TD}>{formatWhen(r.at)}</td>
            <td style={TD}>{PART[r.reason] ?? r.reason}</td>
            <td style={TD}>{r.ref ?? "—"}</td>
            <td style={TD}>{r.model ?? "—"}</td>
            <td style={NUM}>{usd(r.billedUsd)}</td>
            <td style={NUM}>{usd(r.listCostUsd)}</td>
            <td style={NUM}>{tokens(r.inputTokens, r.outputTokens)}</td>
          </tr>
        ))}
      </tbody>
      {rows.length === 200 && (
        <caption style={{ ...MUTED, captionSide: "bottom", textAlign: "left" }}>
          The newest 200 — narrow the period for older ones.
        </caption>
      )}
    </table>
  );
}
